import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldSize,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useState } from "react";
import * as askRedoStyles from "./ask-redo-button.module.css";
import * as css from "./feedback-callout.module.css";

const FEEDBACK_MAX_LENGTH = 500;

/**
 * Serialize the current page to a self-contained HTML snapshot for server-side
 * rendering (Gotenberg). This is cheap — a DOM-to-string clone, no rasterization
 * — so it never freezes the main thread the way an in-browser screenshot does.
 * The feedback popover and all scripts are stripped, and a <base> is injected so
 * the document's relative asset URLs resolve when Chromium renders it. Returns
 * null on any failure so a capture problem never affects the feedback flow.
 */
function capturePageHtml(): File | null {
  try {
    const clone = document.documentElement.cloneNode(true);
    if (!(clone instanceof HTMLElement)) {
      return null;
    }
    for (const el of clone.querySelectorAll(
      "[data-feedback-screenshot-exclude], script, noscript",
    )) {
      el.remove();
    }
    const head = clone.querySelector("head");
    if (head) {
      const base = document.createElement("base");
      base.href = document.baseURI;
      head.prepend(base);
    }
    const html = `<!DOCTYPE html>${clone.outerHTML}`;
    return new File([html], "feedback-page.html", { type: "text/html" });
  } catch {
    return null;
  }
}

/**
 * Step in the feedback flow. After the first comment is submitted the backend
 * may return an AI-generated follow-up question (`followUp`); otherwise we go
 * straight to the final thank-you (`done`).
 */
type FeedbackStep = "feedback" | "followUp" | "done";

/**
 * Fallback follow-up header if the backend signals a follow-up is needed but
 * (defensively) returns no question text.
 */
const DEFAULT_FOLLOW_UP_QUESTION =
  "Got it — what were you trying to do when this came up?";

export interface FeedbackCalloutProps {
  onClose?: () => void;
}

const OrangeAiStarIcon = () => <AiStarIcon className={askRedoStyles.icon} />;

export const FeedbackCallout = memo(function FeedbackCallout({
  onClose,
}: FeedbackCalloutProps) {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const { open: openAskRedoPanel } = useAskRedoSlidePanel();
  const [step, setStep] = useState<FeedbackStep>("feedback");
  const [comment, setComment] = useState("");
  // The original feedback comment (preserved for the Ask Redo prefill once the
  // textarea is reused for the follow-up answer).
  const [feedbackComment, setFeedbackComment] = useState("");
  // Set from the first submit's response so the follow-up answer can be
  // attached to the persisted row, and the real AI question can be shown.
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [followUpQuestion, setFollowUpQuestion] = useState<string | null>(null);
  // The merchant's submitted answer to the follow-up question, preserved so the
  // Ask Redo prefill can include the full feedback → question → answer thread.
  const [followUpAnswer, setFollowUpAnswer] = useState("");

  // Capture the page HTML and attach it to the just-submitted feedback in the
  // background. Capture is cheap (DOM-to-string), so this runs after the submit
  // response and step transition without blocking either; the server renders it
  // to an image (Gotenberg) and embeds it in the Slack message. Best-effort.
  const uploadScreenshot = useCallback(
    (id: string) => {
      const htmlFile = capturePageHtml();
      if (!htmlFile) {
        return;
      }
      void platformRpcClient
        .uploadMerchantFeedbackScreenshot(
          { feedbackId: id },
          { files: [htmlFile] },
        )
        .catch(() => {
          // Best-effort; nothing to surface to the merchant.
        });
    },
    [platformRpcClient],
  );

  const [submitLoad, handleSubmit] = useTriggerLoad(async () => {
    const trimmed = comment.trim();
    if (!trimmed) {
      return;
    }

    if (step === "feedback") {
      const pageUrl = `${window.location.pathname}${window.location.search}`;

      // Fire the feedback (and its LLM classification) immediately so the
      // button shows "Submitting…" and the step advances as soon as the LLM
      // responds.
      const result = await platformRpcClient.submitMerchantFeedback({
        comment: trimmed,
        pageUrl,
      });
      setFeedbackComment(trimmed);

      // The backend's LLM decides whether a follow-up is warranted. Only show
      // the follow-up step when it asks one; otherwise finish.
      if (result.needsFollowUp && result.feedbackId) {
        setFeedbackId(result.feedbackId);
        setFollowUpQuestion(result.followUpQuestion ?? null);
        setStep("followUp");
        setComment("");
      } else {
        setStep("done");
      }

      // Capture + upload the page snapshot in the background once we've
      // advanced, so the screenshot loads while the merchant reads the next
      // step. Every feedback gets one (capture permitting).
      if (result.feedbackId) {
        uploadScreenshot(result.feedbackId);
      }
      return;
    }

    // Follow-up step: attach the answer to the existing feedback row, which
    // folds it into the already-posted Slack message. On error it throws so the
    // error UI shows and the merchant can retry; the answer stays in the field.
    setFollowUpAnswer(trimmed);
    if (feedbackId) {
      await platformRpcClient.submitMerchantFeedbackFollowUp({
        feedbackId,
        answer: trimmed,
      });
    }
    setStep("done");
  });

  const submitting = submitLoad.pending;
  const error =
    !submitting && submitLoad.error
      ? "Something went wrong. Please try again."
      : null;

  const handleAsk = () => {
    // Hand Ask Redo the whole thread — the original feedback plus the follow-up
    // question and the merchant's answer when one was asked — so the concierge
    // has the full context, not just the first comment.
    const segments = [feedbackComment.trim() || comment.trim()];
    if (followUpQuestion?.trim()) {
      segments.push(followUpQuestion.trim());
      if (followUpAnswer.trim()) {
        segments.push(followUpAnswer.trim());
      }
    }
    const conversation = segments.filter(Boolean).join("\n\n");
    onClose?.();
    openAskRedoPanel({
      kind: "prefill",
      input: conversation,
      autoSubmit: true,
    });
  };

  const handleSetComment = (next: string) => {
    setComment(next.slice(0, FEEDBACK_MAX_LENGTH));
  };

  return (
    <Flex
      data-feedback-screenshot-exclude="true"
      dir="column"
      gap="xl"
      /* Modal body contributes 4px below, and none above where it clips the
         input's 4px focus ring. */
      pb="2xl"
      pt="md"
    >
      {step === "done" ? (
        <>
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              Thank you for your feedback!
            </Text>
            <Text fontSize="sm" fontWeight="regular" textColor="secondary">
              We've received your note and our team will look into it. See{" "}
              <a
                className={css.tosLink}
                data-track-id="merchant.top-nav.feedback.common-questions"
                href="https://help.getredo.com/en/"
                rel="noopener noreferrer"
                target="_blank"
              >
                common questions
              </a>{" "}
              for additional support.
            </Text>
          </Flex>
          <Flex align="center" gap="md" justify="flex-end">
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              Continue conversation?
            </Text>
            <RedoButton
              dataTrackId="merchant.top-nav.feedback.ask"
              hierarchy="secondary"
              IconLeading={OrangeAiStarIcon}
              onClick={handleAsk}
              size="sm"
              text="Yes"
            />
          </Flex>
        </>
      ) : (
        <>
          <Flex dir="column" gap="sm">
            {step !== "feedback" && (
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                {followUpQuestion ?? DEFAULT_FOLLOW_UP_QUESTION}
              </Text>
            )}
            <RedoTextareaInputField
              containerHeight="var(--redo-spacing-8xl)"
              placeholder={
                step === "feedback"
                  ? "What's working? What's not?"
                  : "Provide more context here"
              }
              setValue={handleSetComment}
              size={RedoTextareaInputFieldSize.SMALL}
              value={comment}
            />
          </Flex>
          {error && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              {error}
            </Text>
          )}
          <Flex align="center" gap="md" justify="space-between">
            <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
              Feedback provided is subject to your existing services agreement
              or Redo's{" "}
              <a
                className={css.tosLink}
                href="https://redo.com/terms-and-conditions"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms of Service
              </a>
              .
            </Text>
            <RedoButton
              dataTrackId="merchant.top-nav.feedback.submit"
              disabled={comment.trim() === "" || submitting}
              hierarchy="primary"
              onClick={() => handleSubmit()}
              size="sm"
              text={submitting ? "Submitting..." : "Submit"}
            />
          </Flex>
        </>
      )}
    </Flex>
  );
});
